/**
 * useMaaranChat - Custom hook for MAARAN AI chat integration
 *
 * This hook provides session management and chat functionality
 * with support for HITL (Human-in-the-Loop) confirmations.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSession,
  sendChatMessage,
  getSession,
  isAwaitingConfirmation,
  buildAssistantMessage,
} from "@/utils/maaran";

/**
 * Hook for creating a new MAARAN session
 */
export const useCreateSession = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createSession,
    onSuccess: () => {
      // Invalidate sessions list cache
      queryClient.invalidateQueries({ queryKey: ["maaran-sessions"] });
    },
  });

  return {
    createSession: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Hook for sending chat messages to MAARAN
 */
export const useSendMaaranMessage = () => {
  const mutation = useMutation({
    mutationFn: sendChatMessage,
  });

  return {
    sendMessage: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
};

/**
 * Hook for fetching session details
 */
export const useSession = (sessionId) => {
  return useQuery({
    queryKey: ["maaran-session", sessionId],
    queryFn: () => getSession(sessionId),
    enabled: !!sessionId,
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Hook for managing HITL confirmation responses
 */
export const useConfirmationResponse = () => {
  const mutation = useMutation({
    mutationFn: async ({ sessionId, confirmationId, action }) => {
      return sendChatMessage({
        sessionId,
        confirmationId,
        confirmationResponse: action,
      });
    },
  });

  return {
    respond: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Combined hook for full MAARAN chat functionality
 *
 * Features:
 * - Auto session creation on first message
 * - Message sending with loading states
 * - HITL confirmation handling
 * - Response transformation
 */
export const useMaaranChat = () => {
  const { createSession, isCreating } = useCreateSession();
  const { sendMessage, isPending: isSending, error: sendError } = useSendMaaranMessage();
  const { respond: respondToConfirmation, isPending: isResponding } = useConfirmationResponse();

  /**
   * Send a message, creating a session if needed
   * @param {string} message - User message
   * @param {string|null} sessionId - Existing session ID or null
   * @param {Object} options - Additional options (userId, brandId, metadata)
   * @returns {Promise<{response: Object, sessionId: string, message: Object}>}
   */
  const chat = async (message, sessionId = null, options = {}) => {
    let currentSessionId = sessionId;

    // Creat e session if needed
    if (!currentSessionId) {
      const session = await createSession({
        title: message.slice(0, 50),
        mode: options.mode,
        ...options,
      });
      currentSessionId = session.id;
    }

    // Send message
    const response = await sendMessage({
      sessionId: currentSessionId,
      message,
      userId: options.userId,
      brandId: options.brandId,
      metadata: options.metadata,
      mode: options.mode,
      attachments: options.attachments,
    });

    // Build assistant message from response
    const assistantMessage = buildAssistantMessage(response);

    return {
      response,
      sessionId: currentSessionId,
      message: assistantMessage,
      isAwaitingConfirmation: isAwaitingConfirmation(response),
    };
  };

  /**
   * Approve or reject a HITL confirmation
   * @param {string} sessionId - Session ID
   * @param {string} confirmationId - Confirmation ID
   * @param {string} action - "approved" or "rejected"
   * @returns {Promise<{response: Object, message: Object}>}
   */
  const handleConfirmation = async (sessionId, confirmationId, action) => {
    const response = await respondToConfirmation({
      sessionId,
      confirmationId,
      action,
    });

    const assistantMessage = buildAssistantMessage(response);

    return {
      response,
      message: assistantMessage,
    };
  };

  return {
    chat,
    handleConfirmation,
    isPending: isCreating || isSending,
    isResponding,
    error: sendError,
  };
};

export default useMaaranChat;